// The single door to previous calls.
//
// Unlike every other endpoint this app calls, /inspections is authenticated:
// it holds records of customer conversations, so every request carries the
// staff session token as x-api-key. The guest view never touches this module.

import { endpoints } from '../config/endpoints';
import { getToken } from '../auth/session';

export type RecordingStatus = 'pending' | 'stored' | 'failed';

/**
 * open      joinable, and in the lobby
 * completed it ran and ended
 * abandoned created, never taken, no longer shown
 */
export type CallState = 'open' | 'completed' | 'abandoned';

/** One call, as the list shows it. */
export type Inspection = {
  id: string;
  callId: string;
  phoneNumber: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  checklistName: string | null;
  estimatedPriceCents: number | null;
  acceptedBy: string | null;
  fieldCount: number;
  answeredCount: number;
  hasTranscript: boolean;
  /** Null when the call produced no recording, which is the normal case. */
  recordingStatus: RecordingStatus | null;
  state: CallState;
  /** What the creating app called it - a name, an address, a claim number. */
  label: string | null;
  /**
   * The company, when whatever raised the room knew it. Null for rooms this app
   * made itself, which have nobody to ask until an estimator picks one.
   */
  orgId: string | null;
  createdByApp: string | null;
  createdAt: string | null;
  /** Open, and nobody has joined yet. The lobby's headline distinction. */
  waiting: boolean;
  /** When the customer was last texted their link. Null until they were. */
  invitedAt: string | null;
};

export type TranscriptLine = {
  speaker: string | null;
  atMs: number | null;
  text: string;
};

export type InspectionField = {
  fieldKey: string | null;
  label: string;
  value: string | null;
  source: 'extracted' | 'confirmed' | 'manual';
};

export type InspectionRecording = {
  id: string;
  status: RecordingStatus;
  bytes: number | null;
  problem: string | null;
};

/** One call, in full. */
export type InspectionDetail = {
  id: string;
  callId: string;
  phoneNumber: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  checklistId: string | null;
  checklistName: string | null;
  estimatedPriceCents: number | null;
  acceptedBy: string | null;
  fields: InspectionField[];
  transcript: TranscriptLine[];
  recordings: InspectionRecording[];
};

export type InspectionPage = {
  inspections: Inspection[];
  total: number;
  limit: number;
  offset: number;
  /**
   * The companies this inspector's lobby covers, as the endpoint resolved
   * them. Null when unrestricted. Sent so the lobby can offer a filter rather
   * than making somebody work out which rooms are theirs.
   */
  coveredOrgs?: string[] | null;
};

export type Playback = {
  playbackUrl: string;
  downloadUrl: string;
  expiresIn: number;
};

export type Result<T> = { data: T | null; problem: string | null };

const EXPIRED = 'Your session has expired. Sign in again to see previous calls.';

async function request<T>(path: string): Promise<Result<T>> {
  if (!endpoints.inspections) {
    return {
      data: null,
      problem: 'This build has no previous-calls endpoint, so calls cannot be listed.',
    };
  }

  const token = getToken();
  if (!token) {
    // Not an error worth alarming anyone with - the session simply lapsed, and
    // the app's own sign-in handling takes it from here.
    return { data: null, problem: EXPIRED };
  }

  let response: Response;
  try {
    response = await fetch(`${endpoints.inspections.replace(/\/+$/, '')}/${path}`, {
      headers: { 'x-api-key': token },
    });
  } catch {
    return { data: null, problem: 'Could not reach the previous-calls service.' };
  }

  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    if (response.status === 401) return { data: null, problem: EXPIRED };
    const error = (parsed as Record<string, unknown> | null)?.error;
    return {
      data: null,
      problem: typeof error === 'string' ? error : `The service returned ${response.status}.`,
    };
  }

  return { data: parsed as T, problem: null };
}

export const InspectionsStore = {
  list(offset = 0, limit = 25): Promise<Result<InspectionPage>> {
    return request<InspectionPage>(`?offset=${offset}&limit=${limit}`);
  },

  /**
   * The record for one call, by its call id.
   *
   * The invite endpoint is addressed by row id, which the call view does not
   * have - it knows which call it is on, not the row recording it. The row is
   * created when the call is joined, so this is a lookup rather than a search.
   */
  async byCallId(callId: string): Promise<Result<Inspection | null>> {
    const { data, problem } = await request<InspectionPage>(
      `?callId=${encodeURIComponent(callId)}&limit=1`,
    );
    if (problem) return { data: null, problem };
    return { data: data?.inspections?.[0] ?? null, problem: null };
  },

  /**
   * Attach a phone number to a call.
   *
   * A call started by hand has nobody's number on it, so there is nothing to
   * text until somebody says who to text. Sent without startedAt, which the
   * endpoint reads as a detail about the call rather than a claim on it.
   */
  async setPhone(callId: string, phoneNumber: string): Promise<{ problem: string | null }> {
    if (!endpoints.inspections) {
      return { problem: 'This build has no previous-calls endpoint.' };
    }
    const token = getToken();
    if (!token) return { problem: 'Your session has expired. Sign in again.' };

    try {
      const response = await fetch(`${endpoints.inspections.replace(/\/+$/, '')}/`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-api-key': token },
        body: JSON.stringify({ callId, phoneNumber: phoneNumber.trim() || null }),
      });
      if (!response.ok) return { problem: `The number was not saved (${response.status}).` };
      return { problem: null };
    } catch {
      return { problem: 'Could not reach the service, so the number was not saved.' };
    }
  },

  /**
   * The lobby queue: rooms open, oldest first.
   *
   * Oldest first because this is a queue - the room at the top has waited
   * longest and is the next one to take. Previous calls asks the same endpoint
   * for the opposite order, which is why the order is stated rather than left
   * to a default.
   */
  open(limit = 50): Promise<Result<InspectionPage>> {
    return request<InspectionPage>(`?state=open&order=oldest&limit=${limit}`);
  },

  /**
   * Text the customer their guest link, on the company's own line.
   *
   * The link itself is built by the endpoint, not sent from here - it is
   * reachable by anything holding a staff session, and a caller-supplied URL
   * would make it a way to text customers any link at all from the company's
   * number.
   */
  async invite(id: string): Promise<{ problem: string | null; repeat: boolean }> {
    if (!endpoints.inspections) {
      return { problem: 'This build has no previous-calls endpoint.', repeat: false };
    }
    const token = getToken();
    if (!token) return { problem: 'Your session has expired. Sign in again.', repeat: false };

    try {
      const response = await fetch(
        `${endpoints.inspections.replace(/\/+$/, '')}/${encodeURIComponent(id)}/invite`,
        { method: 'POST', headers: { 'x-api-key': token } },
      );
      const parsed = (await response.json().catch(() => null)) as
        | { error?: unknown; repeat?: boolean }
        | null;
      if (!response.ok) {
        const error = parsed?.error;
        return {
          // The endpoint's refusals name what is wrong - no number, no company,
          // an unconfigured line - and are worth showing as they arrive.
          problem: typeof error === 'string' ? error : `The service returned ${response.status}.`,
          repeat: false,
        };
      }
      return { problem: null, repeat: Boolean(parsed?.repeat) };
    } catch {
      return { problem: 'Could not reach the service, so nothing was sent.', repeat: false };
    }
  },

  /**
   * Put a room in the lobby without joining it.
   *
   * Sends no startedAt, which is what the endpoint reads as "somebody is asking
   * for a call" rather than "somebody is on one" - so the room waits, unclaimed,
   * until whoever takes it actually joins.
   */
  async createRoom(
    callId: string,
    label: string,
    phoneNumber = '',
    orgId = '',
  ): Promise<{ problem: string | null }> {
    if (!endpoints.inspections) {
      return { problem: 'This build has no previous-calls endpoint, so the room was not created.' };
    }
    const token = getToken();
    if (!token) return { problem: 'Your session has expired. Sign in again.' };

    try {
      const response = await fetch(`${endpoints.inspections.replace(/\/+$/, '')}/`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-api-key': token },
        body: JSON.stringify({
          callId,
          label: label.trim() || null,
          // Sent as typed. The endpoint stores what it is given, and reshaping
          // a number on the way in would mean the record disagreed with
          // whatever the person actually wrote down.
          phoneNumber: phoneNumber.trim() || null,
          // Which lobby it belongs in. A room without a company matches no
          // inspector's coverage, so it is created and then invisible - which
          // is exactly what happened when lobbies became per-company and this
          // still sent nothing.
          orgId: orgId.trim() || null,
          createdByApp: 'Virtual Inspection',
        }),
      });
      if (!response.ok) {
        let detail = `The service returned ${response.status}.`;
        try {
          const parsed = (await response.json()) as { error?: unknown };
          if (typeof parsed.error === 'string') detail = parsed.error;
        } catch {
          /* the status is enough */
        }
        return { problem: `The room was not created. ${detail}` };
      }
      return { problem: null };
    } catch {
      return { problem: 'Could not reach the service, so the room was not created.' };
    }
  },

  /** One call in full: every field and the whole transcript. */
  detail(id: string): Promise<Result<InspectionDetail>> {
    return request<InspectionDetail>(encodeURIComponent(id));
  },

  /**
   * Signed URLs for one recording, good for an hour.
   *
   * Fetched when a recording is opened rather than with the list: signing fifty
   * at a time would mean fifty URLs granting access to customer video, most of
   * them never used.
   */
  playback(inspectionId: string, recordingId: string): Promise<Result<Playback>> {
    return request<Playback>(
      `${encodeURIComponent(inspectionId)}/recordings/${encodeURIComponent(recordingId)}`,
    );
  },
};
